import { and, eq, ilike, or } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useCallback } from 'react'
import { FlatList, StyleSheet } from 'react-native'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useStore } from '@/lib/pocketbase'
import { useCustomersSearchQuery } from '@/stores/customers-search-store'
import type { Customer } from '@/types/customers'
import { useCollectionRefresh } from '@/ui/CollectionRefreshControl'
import { CustomerCard } from '@/ui/customers/CustomerCard'
import { CustomersEmptyState } from '@/ui/customers/CustomersEmptyState'
import { CustomersListHeader } from '@/ui/customers/CustomersListHeader'
import { ListWrapper } from '@/ui/ListWrapper'
import { LoadingIndicator } from '@/ui/LoadingIndicator'

export default function CustomersPage() {
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const router = useRouter()
    const { org, isLoading: isAuthLoading } = useCurrentOrg()
    const [customersCollection, addressesCollection] = useStore('customers', 'addresses')

    const searchQuery = useCustomersSearchQuery()
    const { refreshControl } = useCollectionRefresh(['customers'])

    const { isLoading: isAllCustomersLoading, data: allCustomers = [] } = useLiveQuery(q =>
        q
            .from({ customers: customersCollection })
            .where(({ customers }) => eq(customers.org, org?.id))
    )

    const { data: customerRows = [] } = useLiveQuery(
        q =>
            q
                .from({ customer: customersCollection })
                .where(({ customer }) => {
                    const conditions = [eq(customer.org, org?.id)]

                    if (searchQuery) {
                        const pattern = `%${searchQuery}%`
                        conditions.push(
                            or(ilike(customer.name, pattern), ilike(customer.identifier, pattern))
                        )
                    }

                    if (conditions.length === 1) {
                        return conditions[0]
                    }
                    return and(...(conditions as [(typeof conditions)[0], (typeof conditions)[0]]))
                })
                .join(
                    { address: addressesCollection },
                    ({ customer, address }) => eq(customer.address, address.id),
                    'left'
                )
                .orderBy(({ customer }) => customer.created, 'desc'),
        [org?.id, searchQuery]
    )

    // Rehydrate `customer.expand.address` from the joined row. Necessary
    // because pbtsdb 0.4+ doesn't refetch on mutation, so the realtime SSE
    // event delivers customer records without expand.
    const customers = customerRows
        .map(({ customer, address }) =>
            customer
                ? ({
                      ...customer,
                      expand: { ...(customer as { expand?: object }).expand, address },
                  } as typeof customer)
                : null
        )
        .filter((c): c is NonNullable<typeof c> => !!c)

    const isInitialLoading = isAuthLoading || isAllCustomersLoading
    const hasAnyCustomers = allCustomers.length > 0

    const handleAddCustomer = useCallback(() => {
        router.push(`/${params.orgSlug}/settings/customers/new`)
    }, [router, params.orgSlug])

    if (isInitialLoading) {
        return (
            <ListWrapper>
                <LoadingIndicator />
            </ListWrapper>
        )
    }

    return (
        <ListWrapper>
            <FlatList
                data={customers}
                keyExtractor={customer => customer.id}
                renderItem={({ item }) => <CustomerCard customer={item as unknown as Customer} />}
                ListHeaderComponent={hasAnyCustomers ? <CustomersListHeader /> : null}
                ListEmptyComponent={
                    <CustomersEmptyState
                        hasAnyCustomers={hasAnyCustomers}
                        onAddCustomer={handleAddCustomer}
                    />
                }
                contentContainerStyle={styles.container}
                showsVerticalScrollIndicator={false}
                refreshControl={refreshControl}
            />
        </ListWrapper>
    )
}

const styles = StyleSheet.create({
    container: {
        padding: 16,
        gap: 12,
    },
})
